// src/components/sections/ClientLinks.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { 
  Box, 
  Typography,
  IconButton,
  Tooltip
} from '@mui/material';
import LaunchIcon from '@mui/icons-material/Launch';
import AssessmentIcon from '@mui/icons-material/Assessment';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LanguageIcon from '@mui/icons-material/Language';

const ClientLinks = ({ 
  clientId,
  portalUrl,
  reportsUrl,
  websiteUrl
}) => {
  const handleLinkClick = (url, e) => {
    e.stopPropagation();
    if (url && url !== '#') {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Box sx={{ p: 1.5, bgcolor: '#1A1A1A' }}>
      <Typography variant="subtitle2" sx={{ 
        fontSize: '0.7rem', 
        fontWeight: 'bold', 
        mb: 1,
        letterSpacing: '0.5px',
        color: 'text.primary'
      }}>
        CLIENT RESOURCES
      </Typography>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}>
        {/* Client Portal */}
        <Tooltip title="Client Portal" arrow>
          <IconButton
            size="small"
            onClick={(e) => handleLinkClick(portalUrl, e)}
            sx={{ 
              color: portalUrl && portalUrl !== '#' ? '#64B5F6' : 'text.disabled',
              '&:hover': { 
                color: portalUrl && portalUrl !== '#' ? '#90CAF9' : 'text.disabled',
                bgcolor: 'rgba(100, 181, 246, 0.1)' 
              },
              cursor: portalUrl && portalUrl !== '#' ? 'pointer' : 'default'
            }}
            disabled={!portalUrl || portalUrl === '#'}
          >
            <DashboardIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* Client Reports */}
        <Tooltip title="Client Reports" arrow>
          <IconButton
            size="small"
            onClick={(e) => handleLinkClick(reportsUrl, e)}
            sx={{ 
              color: reportsUrl && reportsUrl !== '#' ? '#03DAC6' : 'text.disabled',
              '&:hover': { 
                color: reportsUrl && reportsUrl !== '#' ? '#4DD0E1' : 'text.disabled',
                bgcolor: 'rgba(3, 218, 198, 0.1)' 
              },
              cursor: reportsUrl && reportsUrl !== '#' ? 'pointer' : 'default'
            }}
            disabled={!reportsUrl || reportsUrl === '#'}
          >
            <AssessmentIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* Client Website */}
        <Tooltip title="Client Website" arrow>
          <IconButton
            size="small"
            onClick={(e) => handleLinkClick(websiteUrl, e)}
            sx={{ 
              color: websiteUrl && websiteUrl !== '#' ? '#FFCF44' : 'text.disabled',
              '&:hover': { 
                color: websiteUrl && websiteUrl !== '#' ? '#FFE082' : 'text.disabled',
                bgcolor: 'rgba(255, 207, 68, 0.1)' 
              },
              cursor: websiteUrl && websiteUrl !== '#' ? 'pointer' : 'default'
            }}
            disabled={!websiteUrl || websiteUrl === '#'}
          >
            <LanguageIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* External Link */}
        <Tooltip title="External Resources" arrow>
          <IconButton
            size="small"
            onClick={(e) => handleLinkClick(`#/client/${clientId}/external`, e)}
            sx={{ 
              color: 'text.secondary',
              '&:hover': { 
                color: 'primary.main',
                bgcolor: 'rgba(133, 97, 197, 0.1)' 
              }
            }}
          >
            <LaunchIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Link Labels */}
      <Box sx={{ display: 'flex', justifyContent: 'space-around', mt: 0.5 }}>
        <Typography variant="caption" sx={{ 
          fontSize: '0.6rem', 
          color: portalUrl && portalUrl !== '#' ? '#64B5F6' : 'text.disabled',
          textAlign: 'center',
          minWidth: '50px'
        }}>
          Portal
        </Typography>
        <Typography variant="caption" sx={{ 
          fontSize: '0.6rem', 
          color: reportsUrl && reportsUrl !== '#' ? '#03DAC6' : 'text.disabled',
          textAlign: 'center',
          minWidth: '50px'
        }}>
          Reports
        </Typography>
        <Typography variant="caption" sx={{ 
          fontSize: '0.6rem', 
          color: websiteUrl && websiteUrl !== '#' ? '#FFCF44' : 'text.disabled',
          textAlign: 'center',
          minWidth: '50px'
        }}>
          Website
        </Typography>
        <Typography variant="caption" sx={{ 
          fontSize: '0.6rem', 
          color: 'text.secondary',
          textAlign: 'center',
          minWidth: '50px'
        }}>
          External
        </Typography>
      </Box>
    </Box>
  );
};

ClientLinks.propTypes = {
  clientId: PropTypes.string.isRequired,
  portalUrl: PropTypes.string,
  reportsUrl: PropTypes.string,
  websiteUrl: PropTypes.string
};

export default ClientLinks;
